import { useCallback } from "react"
import { useClienteApi } from "../../infra/cliente"
import { useIPPermitidoApi } from "../../infra/ip-permitido"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { useNavigationService } from "../navigation"
import { useUIService } from "../ui"
import { resetClienteSelecionadoState, resetInsertIPPermitidoFormErrorsState, resetInsertIPPermitidoFormState, resetIPPermitidoClientesState, resetIPPermitidoPaginationState, resetIPPermitidoState, resetIPPermitidoTotalState, resetUpdateIPPermitidoFormState, setClienteSelecionadoState, setInsertIPPermitidoFormErrorsState, setInsertIPPermitidoFormIdClienteAInserirState, setInsertIPPermitidoFormIPState, setInsertIPPermitidoFormTouchedState, setInsertIPPermitidoOpenFormState, setIPPermitidoClientesState, setIPPermitidoLimitState, setIPPermitidoPageState, setIPPermitidoPaginationState, setIPPermitidoState, setIPPermitidoTotalState, setUpdateIPPermitidoFormAtivoState, setUpdateIPPermitidoFormErrorsState, setUpdateIPPermitidoFormIdIPPermitidoState, setUpdateIPPermitidoFormIPState, setUpdateIPPermitidoFormTouchedState, setUpdateIPPermitidoOpenFormState, validateInsertIPPermitidoForm, validateUpdateIPPermitidoForm } from "../../application/states/ip-permitido"
import { ICliente } from "../../application/models"

export const useIPPermitidoService = () => {
    // Dispatch
    const dispatch = useAppDispatch()
    // Navigate
    const { navigateTo } = useNavigationService()
    // State
    const { currentUser } = useAppSelector((state) => state.user)
    const {
        insertIPPermitidoForm,
        updateIPPermitidoForm,
        clienteSelecionado,
        ipPermitidoPagination,
    } = useAppSelector((state) => state.ipPermitido)
    // API
    const { getClientesListApi } = useClienteApi()
    const {
        insertIPPermitidoApi,
        getIPPermitidoListByIdClienteApi,
        updateIPPermitidoApi
    } = useIPPermitidoApi()
    // Service
    const { setLoading, setAlert } = useUIService()

    // Functions
    const insertIPPermitido = useCallback(async () => {
        if (currentUser) {
            dispatch(resetInsertIPPermitidoFormErrorsState())

            const loadingId = Math.random().toString()
            try {
                const errors = validateInsertIPPermitidoForm(insertIPPermitidoForm.fields)

                if (errors.length > 0) {
                    dispatch(setInsertIPPermitidoFormErrorsState(errors))

                    dispatch(setInsertIPPermitidoFormTouchedState(true))
                } else {
                    setLoading({
                        id: loadingId,
                        loading: true,
                        message: 'Registrando trava de IP para o cliente...'
                    })

                    await insertIPPermitidoApi({
                        IdClienteAInserir: insertIPPermitidoForm.fields.IdClienteAInserir,
                        IP: insertIPPermitidoForm.fields.IP
                    })

                    dispatch(resetInsertIPPermitidoFormState())
                    setAlert({
                        message: 'IP travado com sucesso',
                        type: 'success'
                    })

                    navigateTo({
                        route: '/dashboard/trava/ip-permitido'
                    })

                    dispatch(setInsertIPPermitidoOpenFormState(false))
                    dispatch(resetIPPermitidoState())
                    dispatch(resetClienteSelecionadoState())
                }

            } catch (error: any) {
                setAlert({
                    message: error.response.data.error,
                    type: 'error'
                })
            } finally {
                setLoading({
                    id: loadingId,
                    loading: false
                })
            }
        }

    }, [currentUser, dispatch, insertIPPermitidoApi, insertIPPermitidoForm.fields, navigateTo, setAlert, setLoading])

    const updateIPPermitido = useCallback(async () => {
        if (currentUser) {
            const loadingId = Math.random().toString()

            try {
                const errors = validateUpdateIPPermitidoForm(updateIPPermitidoForm.fields)

                if (errors.length > 0) {
                    dispatch(setUpdateIPPermitidoFormErrorsState(errors))

                    dispatch(setUpdateIPPermitidoFormTouchedState(true))
                } else {
                    setLoading({
                        id: loadingId,
                        loading: true,
                        message: 'Atualizando trava IP...'
                    })

                    await updateIPPermitidoApi({
                        Ativo: updateIPPermitidoForm.fields.Ativo,
                        IdIPPermitido: updateIPPermitidoForm.fields.IdIPPermitido,
                        IP: updateIPPermitidoForm.fields.IP
                    })

                    dispatch(resetUpdateIPPermitidoFormState())

                    setAlert({
                        message: 'Trava de IP atualizada com sucesso',
                        type: 'success'
                    })

                    navigateTo({
                        route: '/dashboard/trava/ip-permitido'
                    })

                    dispatch(setUpdateIPPermitidoOpenFormState(false))
                    dispatch(resetIPPermitidoState())
                    dispatch(resetClienteSelecionadoState())
                }
            } catch (error: any) {
                setAlert({
                    message: error.response.data.error,
                    type: 'error'
                })
            } finally {
                setLoading({
                    id: loadingId,
                    loading: false
                })
            }
        }
    }, [currentUser, dispatch, navigateTo, setAlert, setLoading, updateIPPermitidoApi, updateIPPermitidoForm.fields])

    const getIPPermitido = useCallback(async () => {
        if (currentUser) {
            if (clienteSelecionado) {
                const loadingId = Math.random().toString()

                try {
                    setLoading({
                        id: loadingId,
                        loading: true,
                        message: 'Buscando IPs bloqueados para o cliente selecionado...'
                    })

                    const getList = await getIPPermitidoListByIdClienteApi({
                        IdClienteBusca: clienteSelecionado.IdCliente,
                        Limit: 10000,
                        Page: 1
                    })

                    dispatch(setIPPermitidoState(getList.ipList))

                    const totalValue = getList.total || 0;

                    dispatch(setIPPermitidoTotalState(totalValue))

                } catch (error: any) {
                    setAlert({
                        message: error.response?.data?.error || 'Erro ao buscar IPs',
                        type: 'error'
                    })
                } finally {
                    setLoading({
                        id: loadingId,
                        loading: false
                    })
                }
            }
        }
    }, [clienteSelecionado, currentUser, dispatch, getIPPermitidoListByIdClienteApi, setAlert, setLoading])

    const findClientes = useCallback(async (data: {
        IdVendedor?: number
        LoginCliente?: string
        NomeFantasia?: string
        RazaoSocial?: string
    }) => {
        if (currentUser) {
            const loadingId = Math.random().toString()

            try {
                setLoading({
                    id: loadingId,
                    loading: true,
                    message: 'Buscando clientes...'
                })

                const clientesList = await getClientesListApi({
                    IdCliente: currentUser.IdCliente,
                    IdUsuario: currentUser.IdUsuario,
                    limit: 25,
                    page: 1,
                    IdVendedor: data.IdVendedor,
                    LoginCliente: data.LoginCliente,
                    NomeFantasia: data.NomeFantasia,
                    RazaoSocial: data.RazaoSocial
                })

                dispatch(setIPPermitidoClientesState({
                    clientes: clientesList.clientes
                }))
            } catch (error: any) {
                setAlert({
                    message: error.response.data.error,
                    type: 'error'
                })
            } finally {
                setLoading({
                    id: loadingId,
                    loading: false
                })
            }
        }
    }, [currentUser, dispatch, getClientesListApi, setAlert, setLoading])

    const setIPPermitidoPagination = useCallback((data: {
        page: number
        limit: number
    }) => {
        dispatch(setIPPermitidoPaginationState({
            limit: data.limit,
            page: data.page
        }))
    }, [dispatch])

    const setIPPermitidoPage = useCallback((data: {
        page: number
    }) => {
        dispatch(setIPPermitidoPageState(data.page))
    }, [dispatch])

    const setIPPermitidoLimit = useCallback((data: {
        limit: number
    }) => {
        dispatch(setIPPermitidoLimitState(data.limit))
    }, [dispatch])

    const resetIPPermitidoPagination = useCallback(() => {
        dispatch(resetIPPermitidoPaginationState())
    }, [dispatch])

    const setInsertIPPermitidoFormIdClienteAInserir = useCallback((data: {
        IdClienteAInserir: number
    }) => {
        dispatch(setInsertIPPermitidoFormIdClienteAInserirState(data.IdClienteAInserir))
    }, [dispatch])

    const setInsertIPPermitidoFormIP = useCallback((data: {
        IP: string
    }) => {
        dispatch(setInsertIPPermitidoFormIPState(data.IP))
    }, [dispatch])

    const setUpdateIPPermitidoFormAtivo = useCallback((data: {
        Ativo: boolean
    }) => {
        dispatch(setUpdateIPPermitidoFormAtivoState(data.Ativo))
    }, [dispatch])

    const setUpdateIPPermitidoFormIP = useCallback((data: {
        IP: string
    }) => {
        dispatch(setUpdateIPPermitidoFormIPState(data.IP))
    }, [dispatch])

    const setInsertIPPermitidoOpenForm = useCallback((data: {
        open: boolean
    }) => {
        if (data.open) {
            if (clienteSelecionado) {
                dispatch(setInsertIPPermitidoOpenFormState(true))
                dispatch(setInsertIPPermitidoFormIdClienteAInserirState(clienteSelecionado.IdCliente))
                navigateTo({
                    route: '/dashboard/trava/ip-permitido/novo'
                })
            }
        } else {
            dispatch(resetInsertIPPermitidoFormState())
            dispatch(setInsertIPPermitidoOpenFormState(false))
            navigateTo({
                route: '/dashboard/trava/ip-permitido'
            })
        }
    }, [clienteSelecionado, dispatch, navigateTo])

    const setUpdateIPPermitidoOpenForm = useCallback((data: {
        open: boolean
        ativo?: boolean
        idIPPermitido?: number
        ip?: string
    }) => {
        if (data.open) {
            if (data.ativo !== undefined && data.idIPPermitido && data.ip) {
                dispatch(setUpdateIPPermitidoOpenFormState(true))
                dispatch(setUpdateIPPermitidoFormAtivoState(data.ativo))
                dispatch(setUpdateIPPermitidoFormIdIPPermitidoState(data.idIPPermitido))
                dispatch(setUpdateIPPermitidoFormIPState(data.ip))
                navigateTo({
                    route: '/dashboard/trava/ip-permitido/atualizar'
                })
            }
        } else {
            dispatch(resetUpdateIPPermitidoFormState())
            dispatch(setUpdateIPPermitidoOpenFormState(false))
            navigateTo({
                route: '/dashboard/trava/ip-permitido'
            })
        }
    }, [dispatch, navigateTo])

    const resetIPPermitidoTotal = useCallback(() => {
        dispatch(resetIPPermitidoTotalState())
    }, [dispatch])

    const setClienteSelecionado = useCallback((data: {
        clienteSelecionado: ICliente
    }) => {
        dispatch(setClienteSelecionadoState(data.clienteSelecionado))
    }, [dispatch])

    const resetClienteSelecionado = useCallback(() => {
        dispatch(resetClienteSelecionadoState())
    }, [dispatch])

    const resetIPPermitidoClientes = useCallback(() => {
        dispatch(resetIPPermitidoClientesState())
    }, [dispatch])

    const resetIPPermitido = useCallback(() => {
        dispatch(resetIPPermitidoState());
    }, [dispatch]);

    return {
        resetIPPermitido,
        resetIPPermitidoClientes,
        insertIPPermitido,
        updateIPPermitido,
        getIPPermitido,
        findClientes,
        setIPPermitidoPagination,
        setIPPermitidoPage,
        setIPPermitidoLimit,
        resetIPPermitidoPagination,
        setInsertIPPermitidoFormIdClienteAInserir,
        setInsertIPPermitidoFormIP,
        setUpdateIPPermitidoFormAtivo,
        setUpdateIPPermitidoFormIP,
        setInsertIPPermitidoOpenForm,
        setUpdateIPPermitidoOpenForm,
        resetIPPermitidoTotal,
        setClienteSelecionado,
        resetClienteSelecionado
    }
}